import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import StepWizard from "components/StepWizard";
import IconSpinner from "components/IconSpinner";
import StepInfo from "components/StepInfo";
import { getUserProfile, updateUserProfile } from "actions/userActions";
import UserProfileForm from "pages/dashboards/individualDashboard/profile/UserProfileForm";
import UploadDocumentation from "pages/dashboards/individualDashboard/profile/UploadDocumentation";
import { filter, isEqual, map, pick, toLower } from "lodash";
import { ID_DOCUMENT } from "statics/docTypes";
import { createLoadingSelector } from "store/selectors";
import { userConstants } from "actions/types";
import { toast } from "react-toastify";
import type { UserProfileInput } from "types/graphql";
import type { RootState } from "types/state";

// The profile slice carries arbitrary backend keys (Record<string, unknown>);
// this component reads/writes only these string fields, so type the local
// userInfo prop/state to the shape it actually consumes.
interface ProfileInfo {
  legalName?: string;
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phoneNumber?: string;
  email?: string;
}

// The userDocuments slice carries docType/docUrl/subDocType at runtime (legacy
// redux shape); the typed Document model exposes type/uri instead. This
// component reads the legacy keys, so type the documents to the shape it
// actually consumes.
interface ProfileDocument {
  docType?: string;
  docUrl: string;
  contentType: string;
  name: string;
  subDocType?: string;
}

const stepToLabels: Record<string, { subheader: string }> = {
  1: {
    subheader: "Update your personal information.",
  },
  2: {
    subheader: "Upload verifying documentation.",
  },
};

const steps = [
  {
    index: 1,
    label: "Update Info",
  },
  {
    index: 2,
    label: "Upload Documentation",
  },
];

const loadingSelector = createLoadingSelector([
  userConstants.USER_PROFILE_UPDATE,
]);

const mapStateToProps = (state: RootState) => ({
  // profile slice is Record<string, unknown> at runtime — cast to the read shape
  userInfo: state.user.profile as ProfileInfo,
  isFetching: loadingSelector(state),
  // legacy userDocuments slice exposes docType/docUrl (see ProfileDocument)
  documents: filter(
    state.userDocuments.documents as unknown as ProfileDocument[],
    { docType: ID_DOCUMENT }
  ),
});

const mapDispatchToProps = {
  getUserProfile,
  updateUserProfile,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
  activeStep: string | number;
  userInfo: ProfileInfo;
}

class UserProfileWorkflow extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      activeStep: "1",
      userInfo: {},
    };
  }

  async componentDidMount() {
    await this.props.getUserProfile(this.props.client as ApolloClient<object>);
    this.setState({ initialFetching: false, userInfo: this.props.userInfo });
  }

  submitUserProfile = () => {
    const payload: UserProfileInput = pick(this.state.userInfo, [
      "legalName",
      "firstName",
      "lastName",
      "address1",
      "address2",
      "city",
      "state",
      "postalCode",
      "phoneNumber",
      "email",
    ]);

    payload.documents = map(this.props.documents, (doc) => {
      return {
        type: ID_DOCUMENT,
        url: doc.docUrl,
        contentType: doc.contentType,
        name: doc.name,
        metadata: { subType: doc.subDocType },
      };
    });

    void this.props
      .updateUserProfile(this.props.client as ApolloClient<object>, payload)
      .then(() => {
        toast.success("Profile updated successfully");
        // PRESERVED BUG (conversion-only): onSuccess is not guaranteed by callers; `!` preserves throw-on-undefined
        this.props.onSuccess!();
      });
  };

  onStepChange = ({ activeStep }: { activeStep: number }) => {
    this.setState({ activeStep });
  };

  updateUserInfo = (userInfo: ProfileInfo) => {
    this.setState({
      userInfo: {
        ...this.state.userInfo,
        ...userInfo,
      },
    });
  };

  checkIfNameChanged = () => {
    const { userInfo } = this.state;
    const { firstName, lastName } = userInfo;
    return (
      firstName !== this.props.userInfo.firstName ||
      lastName !== this.props.userInfo.lastName
    );
  };

  checkIfPhoneChanged = () => {
    const { userInfo } = this.state;
    const { phoneNumber } = userInfo;
    return phoneNumber !== this.props.userInfo.phoneNumber;
  };

  checkIfAddressChanged = () => {
    const userStateAddress = {
      address1: toLower(this.state.userInfo.address1),
      address2: toLower(this.state.userInfo.address2),
      state: toLower(this.state.userInfo.state),
      postalCode: toLower(this.state.userInfo.postalCode),
      city: toLower(this.state.userInfo.city),
    };

    const userOriginalAddress = {
      address1: toLower(this.props.userInfo.address1),
      address2: toLower(this.props.userInfo.address2),
      state: toLower(this.props.userInfo.state),
      postalCode: toLower(this.props.userInfo.postalCode),
      city: toLower(this.props.userInfo.city),
    };

    return !isEqual(userStateAddress, userOriginalAddress);
  };

  render() {
    const activeSectionLabels = stepToLabels[this.state.activeStep];

    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    const nameWasChanged = this.checkIfNameChanged();
    const addressWasChanged = this.checkIfAddressChanged();
    const phoneWasChanged = this.checkIfPhoneChanged();

    return (
      <>
        <p className="steps-header">{activeSectionLabels.subheader}</p>
        <StepInfo steps={steps} activeStepNumber={this.state.activeStep} />
        <StepWizard
          onStepChange={this.onStepChange}
          isLazyMount={true}
          transitions={{
            enterRight: "",
            enterLeft: "",
            exitRight: "",
            exitLeft: "",
          }}
        >
          <UserProfileForm
            userInfo={this.state.userInfo}
            updateUserInfo={this.updateUserInfo}
            stepName={"updateInfo"}
            // onCancel is optional here (PropTypes was not isRequired) but the
            // child declares it required; preserve the original passthrough.
            // @ts-expect-error onCancel may be undefined
            onCancel={this.props.onCancel}
          />
          <UploadDocumentation
            userInfo={this.state.userInfo}
            stepName={"uploadDocuments"}
            addressWasChanged={addressWasChanged}
            nameWasChanged={nameWasChanged}
            phoneWasChanged={phoneWasChanged}
            onSubmit={this.submitUserProfile}
            documents={this.props.documents}
            isUpdating={this.props.isFetching}
          />
        </StepWizard>
      </>
    );
  }
}

export default connector(withApollo<Props>(UserProfileWorkflow));
